import { PlusIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { memo, ReactNode, use } from 'react'
import { useParams } from 'react-router'
import { Button } from '@/components/ui'
import {
  PeriodTableCellContent,
  TableCell,
  TableHeader,
  TableRow,
  FixedTableCell,
  TableCellContent,
  TableHead,
  RowHeader
} from '@/components/ui/table'
import { PeriodType, PeriodTypeQuery, PeriodTypeView, TableHeaderMonth, TableHeaderYear } from '@/types'
import { HeaderSpacer, NavigationTH } from '@/lib/context/navigation'
import { cn } from '@/lib/utils'
import { Show } from '../show'
import { useGetPeriodType } from '@/lib/hooks/useGetPeriodType'
import { PeriodTableHeaderContent } from './table-cell'
import { TableContext, TableRowProvider, TableRowType } from './table-context'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'

type TableRowButtonProps = {
  onClick: () => void
  span?: number
} & React.ComponentProps<typeof TableRow>

const PeriodTableRow = memo(
  ({
    ref,
    type,
    className,
    ...props
  }: React.ComponentPropsWithRef<'tr'> & {
    type: TableRowType
  }) => {
    const context = use(TableContext)
    if (!context) {
      if (import.meta.env.DEV) {
        throw new Error(`PeriodTableRow: Using PeriodTableRow outside of TableProvider`)
      }
      return <TableRow {...props} ref={ref} />
    }

    return (
      <TableRowProvider value={{ type }}>
        <TableRow
          {...props}
          ref={ref}
          className={cn(
            {
              'bg-gray-10 hover:bg-gray-10': type === `output`,
              'bg-blue-50 hover:bg-blue-50': type === `totals` || type === `total-input`,
              'bg-blue-100 hover:bg-blue-100': type === `totals-dark`,
              'bg-transparent hover:bg-transparent': type === `input`
            },
            className
          )}
        />
      </TableRowProvider>
    )
  }
)

PeriodTableRow.displayName = `PeriodTableRow`

const TableRowButton = ({ ref, onClick, span, ...props }: React.ComponentPropsWithRef<'tr'> & TableRowButtonProps) => {
  const { t } = useTranslation(`common`)
  return (
    <TableRow {...props} ref={ref}>
      <TableCell colSpan={span} className='border-b border-b-gray-200 bg-transparent px-0 py-0 text-center'>
        <div className='flex justify-start gap-2'>
          <Button onClick={onClick} size='sm' variant='ghost' className='w-full justify-start rounded-none text-secondary'>
            <PlusIcon size={16} className='mr-2' />
            {t(`table.new_row`)}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

TableRowButton.displayName = `TableRowButton`

/**
 * @deprecated
 */
const GenericTableHeader = ({
  ref,
  years,
  months,
  view,
  queryPeriod,
  period,
  context = false,
  ...props
}: React.ComponentPropsWithRef<'thead'> & {
  years: TableHeaderYear[]
  months: TableHeaderMonth[]
  view: PeriodTypeView
  queryPeriod: PeriodTypeQuery
  period: PeriodType
  context?: boolean
}) => {
  const { t: commonT } = useTranslation(`common`)
  return (
    <TableHeader className='w-full [&_tr]:border-t-0' ref={ref} {...props}>
      <TableRow
        className={cn(`w-full hover:bg-transparent`, {
          'border-b-0! collapse border-b-transparent': view === `monthly`
        })}
      >
        <HeaderSpacer />
        {years.map((year, index) => (
          <NavigationTH
            key={`${year.year}-${year.span}`}
            colSpan={year.span}
            id={`year-${year.year}`}
            data-use-fallback={index === 0}
            data-year={year.year}
            className='h-10'
          >
            <div className='flex flex-col items-center'>
              <span className='text-sm text-secondary'>{`${commonT(`table.fy`)} ${year.year}`}</span>
            </div>
          </NavigationTH>
        ))}
      </TableRow>

      <Show when={view === `monthly`}>
        <TableRow className='border-t-0! text-secondary hover:bg-transparent'>
          <FixedTableCell className='border-t-0! border-r-transparent border-t-transparent'>
            <TableCellContent />
          </FixedTableCell>
          {months.map(month => (
            <TableCell key={`${month.period}-${month.fiscal_year} ${month.period_type}`} className='border-0'>
              <Show
                when={context}
                fallback={
                  <PeriodTableCellContent
                    queryPeriod={queryPeriod}
                    period={period}
                    type={month.period_type ?? `forecast`}
                    className='border-t-0 text-center'
                    fiscal_year={month.fiscal_year}
                  >
                    {month.period}
                  </PeriodTableCellContent>
                }
              >
                <PeriodTableHeaderContent cell_period={month.id}>{month.period}</PeriodTableHeaderContent>
              </Show>
            </TableCell>
          ))}
        </TableRow>
      </Show>
    </TableHeader>
  )
}

GenericTableHeader.displayName = `GenericTableHeader`

const GenericPeriodTableHeader = ({
  ref,
  years,
  months,
  view,
  actualizedPeriods,
  ...props
}: React.ComponentPropsWithRef<'thead'> & {
  years: TableHeaderYear[]
  months: TableHeaderMonth[]
  view: PeriodTypeView
  actualizedPeriods?: string[]
}) => {
  const { t: commonT } = useTranslation(`common`)

  return (
    <TableHeader className='w-full [&_tr]:border-t-0' ref={ref} {...props}>
      <TableRow
        className={cn(`w-full hover:bg-transparent`, {
          'border-b-0! collapse border-b-transparent': view === `monthly`
        })}
      >
        <HeaderSpacer />
        {years.map((year, index) => (
          <NavigationTH
            key={`${year.year}-${year.span}`}
            colSpan={year.span}
            id={`year-${year.year}`}
            data-use-fallback={index === 0}
            data-year={year.year}
            className='h-10'
          >
            <div className='flex flex-col items-center'>
              <span className='text-sm text-secondary'>{`${commonT(`table.fy`)} ${year.year}`}</span>
            </div>
          </NavigationTH>
        ))}
      </TableRow>

      <Show when={view === `monthly`}>
        <TableRow className='border-t-0! text-secondary hover:bg-transparent'>
          <FixedTableCell className='border-t-0! border-r-transparent border-t-transparent'>
            <TableCellContent />
          </FixedTableCell>
          {months.map(month => (
            <TableCell key={`${month.period}-${month.fiscal_year} ${month.period_type}`} className='border-0'>
              <PeriodTableHeaderContent
                fiscal_year={month.fiscal_year}
                cell_period={month.id}
                isActualized={Boolean(actualizedPeriods?.includes(month.id))}
              >
                {month.period}
              </PeriodTableHeaderContent>
            </TableCell>
          ))}
        </TableRow>
      </Show>
    </TableHeader>
  )
}

GenericPeriodTableHeader.displayName = `GenericPeriodTableHeader`

/**
 * @deprecated
 */
const TotalsResultTableHeader = ({
  ref,
  years,
  months,
  view,
  headerCell,
  context = false,
  ...props
}: Omit<React.ComponentProps<typeof GenericTableHeader>, 'queryPeriod' | 'period'> & {
  headerCell?: ReactNode
  ref?: React.RefObject<HTMLTableSectionElement>
  context?: boolean
}) => {
  const { t: commonT } = useTranslation(`common`)
  const { id: financialModelId } = useParams<{ id: string }>()
  const getPeriodType = useGetPeriodType(financialModelId!)

  return (
    <TableHeader className='w-full [&_tr]:border-t-0' ref={ref} {...props}>
      <TableRow
        className={cn(`w-full hover:bg-transparent`, {
          'border-b-0! collapse border-b-transparent': view === `monthly`
        })}
      >
        {headerCell ?? <HeaderSpacer className='border-b-0' />}
        {years.map((year, index) => (
          <NavigationTH
            key={`${year.year}-${year.span}`}
            colSpan={year.span}
            id={`year-${year.year}`}
            data-use-fallback={index === 0}
            data-year={year.year}
            className='h-10'
          >
            <div
              className={cn(`flex flex-col`, {
                'items-start': view === `monthly`,
                'items-center': view === `yearly`
              })}
            >
              <span className='text-sm text-secondary'>{`${commonT(`table.fy`)} ${year.year}`}</span>
            </div>
          </NavigationTH>
        ))}
      </TableRow>
      {view === `monthly` ? (
        <TableRow className='border-t-0! text-secondary hover:bg-transparent'>
          {headerCell ?? (
            <FixedTableCell className='border-t-0! border-r-transparent border-t-transparent'>
              <TableCellContent />
            </FixedTableCell>
          )}
          {months.map(month => (
            <TableCell key={month.period} className='border-0'>
              <Show
                when={context}
                fallback={
                  <PeriodTableCellContent
                    queryPeriod={`all`}
                    period={getPeriodType(month.id)}
                    type={getPeriodType(month.id)}
                    className='border-0 text-center'
                  >
                    {month.period}
                  </PeriodTableCellContent>
                }
              >
                <PeriodTableHeaderContent className='border-0 text-center' cell_period={month.id}>
                  {month.period}
                </PeriodTableHeaderContent>
              </Show>
            </TableCell>
          ))}
        </TableRow>
      ) : null}
    </TableHeader>
  )
}

TotalsResultTableHeader.displayName = `TotalsResultTableHeader`

const TotalsResultPeriodTableHeader = ({
  ref,
  years,
  months,
  view,
  headerCell,
  ...props
}: Omit<React.ComponentProps<typeof GenericPeriodTableHeader>, 'queryPeriod' | 'period'> & {
  headerCell?: ReactNode
  ref?: React.RefObject<HTMLTableSectionElement>
}) => {
  const { t: commonT } = useTranslation(`common`)

  return (
    <TableHeader className='w-full [&_tr]:border-t-0' ref={ref} {...props}>
      <TableRow
        className={cn(`w-full hover:bg-transparent`, {
          'border-b-0! collapse border-b-transparent': view === `monthly`
        })}
      >
        {headerCell ?? <HeaderSpacer className='border-b-0' />}
        {years.map((year, index) => (
          <NavigationTH
            key={`${year.year}-${year.span}`}
            colSpan={year.span}
            id={`year-${year.year}`}
            data-use-fallback={index === 0}
            data-year={year.year}
            className='h-10'
          >
            <div
              className={cn(`flex flex-col`, {
                'items-start': view === `monthly`,
                'items-center': view === `yearly`
              })}
            >
              <span className='text-sm text-secondary'>{`${commonT(`table.fy`)} ${year.year}`}</span>
            </div>
          </NavigationTH>
        ))}
      </TableRow>
      {view === `monthly` ? (
        <TableRow className='border-t-0! text-secondary hover:bg-transparent'>
          {headerCell ?? (
            <FixedTableCell className='border-t-0! border-r-transparent border-t-transparent'>
              <TableCellContent />
            </FixedTableCell>
          )}
          {months.map(month => (
            <TableCell key={month.period} className='border-0'>
              <PeriodTableHeaderContent className='border-0 text-center' cell_period={month.id}>
                {month.period}
              </PeriodTableHeaderContent>
            </TableCell>
          ))}
        </TableRow>
      ) : null}
    </TableHeader>
  )
}

TotalsResultPeriodTableHeader.displayName = `TotalsResultPeriodTableHeader`

const CXGRTableHeader = ({
  ref,
  view,
  emptyRows = 0,
  ...props
}: React.ComponentPropsWithRef<'thead'> & {
  view: PeriodTypeView
  emptyRows?: number
}) => {
  const { t: commonT } = useTranslation(`common`)
  const { id: financialModelId } = useParams<{ id: string }>()
  const hasHistoricalData = useFinancialModelSettingsStore(state => state.settings[financialModelId!]?.timeframe?.historical.isActive) ?? false

  return (
    <TableHeader ref={ref} {...props}>
      <TableRow className='hover:bg-transparent'>
        <Show when={hasHistoricalData}>
          <TableHead className='h-10 text-center'>
            {commonT(`table.headers.historical`, {
              cagr_type: view === `yearly` ? `CAGR` : `CMGR`
            })}
          </TableHead>
        </Show>
        <TableHead className='h-10 text-center'>
          {commonT(`table.headers.forecast`, {
            cagr_type: view === `yearly` ? `CAGR` : `CMGR`
          })}
        </TableHead>
      </TableRow>
      {Array.from({
        length: view === `yearly` ? emptyRows - 1 : emptyRows
      }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow className='hover:bg-transparent' key={index}>
          <TableHead className='mt-0.5 h-11' />
        </TableRow>
      ))}
    </TableHeader>
  )
}

CXGRTableHeader.displayName = `CXGRTableHeader`

const TableWithCagrLayout = ({ ref, ...props }: React.ComponentPropsWithRef<'div'>) => {
  const { className, ...rest } = props
  const { id: financialModelId } = useParams<{ id: string }>()
  const hasHistoricalData = useFinancialModelSettingsStore(state => state.settings[financialModelId!]?.timeframe?.historical.isActive) ?? false
  return (
    <div
      className={cn(
        `grid grid-rows-1 items-stretch gap-2`,
        {
          'grid-cols-[5fr_1fr]': hasHistoricalData,
          'grid-cols-[11fr_1fr]': !hasHistoricalData
        },
        className
      )}
      ref={ref}
      {...rest}
    />
  )
}

type RowSpacerProps = React.ComponentProps<typeof TableRow> & {
  colSpan: number
  variant?: `small` | `medium` | `large`
}

const RowSpacer = ({ colSpan, className, variant = `medium`, ...props }: RowSpacerProps) => (
  <TableRow className={cn(`bg-transparent hover:bg-transparent`, className)} {...props}>
    <TableCell
      colSpan={colSpan}
      className={cn(`h-5`, {
        'h-5': variant === `small`,
        'h-10': variant === `medium`,
        'h-15': variant === `large`
      })}
    />
  </TableRow>
)

RowSpacer.displayName = `RowSpacer`

const RowDevider = memo(
  ({
    colSpan,
    className,
    ...props
  }: React.ComponentPropsWithRef<typeof TableRow> & {
    colSpan: number
  }) => (
    <TableRow className={cn(`bg-transparent hover:bg-transparent`, className)} {...props}>
      <TableCell colSpan={colSpan} className='h-1 border-none bg-gray-200' />
    </TableRow>
  )
)
RowDevider.displayName = `RowDevider`

const RowSectionHeaderTitle = ({
  header,
  span,
  className,
  ...props
}: React.ComponentPropsWithRef<'tr'> & {
  header: string
  span: number
}) => (
  <TableRow className={cn(`border-t-0 bg-transparent hover:bg-transparent`, className)} {...props}>
    <FixedTableCell className='shadow-none! border-none'>
      <RowHeader type='output'>{header}</RowHeader>
    </FixedTableCell>
    <TableCell colSpan={span} className='bg-white' />
  </TableRow>
)

RowSectionHeaderTitle.displayName = `RowSectionHeaderTitle`

export {
  TableRowButton,
  GenericTableHeader,
  GenericPeriodTableHeader,
  TableWithCagrLayout,
  CXGRTableHeader,
  TotalsResultTableHeader,
  TotalsResultPeriodTableHeader,
  PeriodTableRow,
  RowSpacer,
  RowDevider,
  RowSectionHeaderTitle
}
